import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import FloatingChatButton from "../floatingchat/FloatingChat";
import "./napelemes-kisokos.scss";
import CompanyContact from "../companycontact/CompanyContact";

const NapelemesKisokosModel = () => {
  const chatButton = useRef();
  const [activeSection, setActiveSection] = useState("");

  // Add Intersection Observer to track which section is visible
  useEffect(() => {
    const sections = document.querySelectorAll(".napelemes-kisokos__section");

    const options = {
      root: null, // viewport as root
      rootMargin: "-250px 0px", // match the offset used for scrolling
      threshold: 0.2, // 20% of the section must be visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // When a section is in view, set it as active
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, []);

  // Add scroll handling for the navigation links
  useEffect(() => {
    const handleNavClick = (event) => {
      // Check if the click was on an anchor link in our navigation
      const link = event.target.closest(".napelemes-kisokos__nav a");
      if (!link) return;

      const targetId = link.getAttribute("href");
      if (!targetId || !targetId.startsWith("#")) return;

      event.preventDefault();

      const targetElement = document.querySelector(targetId);
      if (!targetElement) return;

      // Calculate the target scroll position
      const targetPosition =
        targetElement.getBoundingClientRect().top + window.scrollY - 225;

      // Scroll to the target position with smooth behavior
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    };

    const navElement = document.querySelector(".napelemes-kisokos__nav");
    if (navElement) {
      navElement.addEventListener("click", handleNavClick);
    }

    // Cleanup event listener on component unmount
    return () => {
      if (navElement) {
        navElement.removeEventListener("click", handleNavClick);
      }
    };
  }, []);

  return (
    <>
      <div className="solar">
        <div className="solar__hero fade-bottom">
          <img
            src={require(`../../assets/solar/napelemes-kisokos.webp`)}
            className="full-imagee"
            alt="hero"></img>
          <div
            className="solar__hero__info"
            style={{ color: "#fff", zIndex: 1, marginTop: "40dvh" }}>
            <motion.h1
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}>
              Napelemes Kisokos
            </motion.h1>
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}>
              Avagy minden, amit egy laikusnak tudnia érdemes a napelemes
              rendszerekről
            </motion.h2>
          </div>
        </div>

        <div className="napelemes-kisokos">
          <div className="napelemes-kisokos__content">
            <div className="napelemes-kisokos__nav">
              <ul>
                <li>
                  <a
                    href="#section1"
                    className={activeSection === "section1" ? "active" : ""}>
                    1. Hogyan termel áramot a Nap?
                  </a>
                </li>
                <li>
                  <a
                    href="#section2"
                    className={activeSection === "section2" ? "active" : ""}>
                    2. Gyakori alapfogalmak
                  </a>
                </li>
                <li>
                  <a
                    href="#section3"
                    className={activeSection === "section3" ? "active" : ""}>
                    3. Előnyök és kihívások
                  </a>
                </li>
                <li>
                  <a
                    href="#section4"
                    className={activeSection === "section4" ? "active" : ""}>
                    4. Tájolás és tartószerkezet
                  </a>
                </li>
                <li>
                  <a
                    href="#section5"
                    className={activeSection === "section5" ? "active" : ""}>
                    5. Nettó vs. Bruttó elszámolás
                  </a>
                </li>
                <li>
                  <a
                    href="#section6"
                    className={activeSection === "section6" ? "active" : ""}>
                    6. Inverter-kisokos
                  </a>
                </li>
                <li>
                  <a
                    href="#section7"
                    className={activeSection === "section7" ? "active" : ""}>
                    7. Miért pont Deye hibrid inverter?
                  </a>
                </li>
                <li>
                  <a
                    href="#section8"
                    className={activeSection === "section8" ? "active" : ""}>
                    8. Telepítési folyamat
                  </a>
                </li>
                <li>
                  <a
                    href="#section9"
                    className={activeSection === "section9" ? "active" : ""}>
                    9. Gyakori kérdések
                  </a>
                </li>
                <li>
                  <a
                    href="#section10"
                    className={activeSection === "section10" ? "active" : ""}>
                    10. Okos megoldások
                  </a>
                </li>
              </ul>
            </div>

            {/* Section 1 */}
            <section id="section1" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                1. Hogyan termel áramot a Nap?
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                Fotovoltaikus (PV) elv – a szilícium‑alapú cellák a napsugárzást
                közvetlenül egyenárammá (DC) alakítják. Az inverter váltóáramot
                (AC) készít belőle, amit már a háztartási fogyasztók
                használhatnak.
              </motion.p>
              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}>
                Alap építőelemek:
              </motion.h3>
              <motion.ol
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}>
                <li>PV‑panelek – monokristályos modulok 20–22% hatásfokkal.</li>
                <li>
                  Inverter – a rendszer „szíve", igazítja a hálózathoz a
                  feszültséget/frekvenciát.
                </li>
                <li>
                  Okos fogyasztásmérő – méri a termelést ↔ fogyasztást
                  (nettó/bruttó elszámolás).
                </li>
                <li>
                  Akkumulátor (opcionális) – tárolja a felesleget, vagy
                  szigetüzemet biztosít áramszünetkor.
                </li>
                <li>
                  Szerelési rendszer és védelem – alumínium tartósínek,
                  túlfeszültség‑ és tűzvédelmi elemek.
                </li>
              </motion.ol>
            </section>

            {/* Section 2 */}
            <section id="section2" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                2. Gyakori alapfogalmak
              </motion.h2>
              <motion.div
                className="napelemes-kisokos__table"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Rövidítés</th>
                      <th>Jelentés</th>
                      <th>Miért fontos?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>kWp</td>
                      <td>Peak teljesítmény</td>
                      <td>A modulok csúcsteljesítménye (STC).</td>
                    </tr>
                    <tr>
                      <td>kWh</td>
                      <td>Kilowattóra</td>
                      <td>A megtermelt/felhasznált energia mennyisége.</td>
                    </tr>
                    <tr>
                      <td>MPPT</td>
                      <td>Maximum Power Point Tracking</td>
                      <td>
                        Az inverter folyamatosan keresi a legnagyobb
                        teljesítménypontot a változó napfényben – +5–15% hozam.
                      </td>
                    </tr>
                    <tr>
                      <td>AC/DC</td>
                      <td>Váltó‑ / Egyenáram</td>
                      <td>A PV‑panel DC‑t, az otthon AC‑t „szeret".</td>
                    </tr>
                    <tr>
                      <td>Nettó/Bruttó elszámolás</td>
                      <td>Mérési‑számlázási modell</td>
                      <td>
                        HU‑ban 2024‑től új betáplálási szabályok érvényesek.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>
            </section>

            {/* Section 3 */}
            <section id="section3" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                3. Előnyök és kihívások
              </motion.h2>

              <motion.div
                className="napelemes-kisokos__cards"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                <div className="napelemes-kisokos__card">
                  <h3>Energiaár függő megtérülés</h3>
                  <ul>
                    <li>
                      Zöld energia – évente ~0,6 t CO₂ megtakarítás egy 5 kWp
                      rendszerrel.
                    </li>
                    <li>
                      Energiafüggetlenség – akku + hibrid inverter =
                      áramszünet‑biztos otthon.
                    </li>
                    <li>
                      Ingatlan‑értéknövelés – 4‑7 %-kal magasabb piaci érték.
                    </li>
                  </ul>
                </div>

                <div className="napelemes-kisokos__card">
                  <h3>Amire figyelni kell</h3>
                  <ul>
                    <li>Kezdő beruházás – panel + inverter + munkadíj</li>
                    <li>Tetőszerkezet, tájolás (dél ±45°).</li>
                    <li>
                      Engedélyeztetés és hálózati korlátok – betáplálási limit,
                      bruttó tarifák.
                    </li>
                    <li>
                      Éves termelési ingadozás – 15‑20 % a napsütéses órák
                      függvényében.
                    </li>
                  </ul>
                </div>
              </motion.div>
            </section>

            {/* Section 4 */}
            <section id="section4" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                4. Tájolás és tartószerkezet – miért kulcsfontosságú?
              </motion.h2>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                4.1 Tájolás (irány + dőlésszög)
              </motion.h3>

              <motion.div
                className="napelemes-kisokos__table"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Tényező</th>
                      <th>Optimális HU érték</th>
                      <th>Mit befolyásol?</th>
                      <th>Tipikus veszteség, ha eltér</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Azimut (irány)</td>
                      <td>180° (dél) ±45°</td>
                      <td>Napi termelési görbe csúcspontja</td>
                      <td>±60°‑nál már ‑5–8 %/év</td>
                    </tr>
                    <tr>
                      <td>Dőlésszög</td>
                      <td>35° (+ 5° télen, ‑5° nyáron)</td>
                      <td>Éves össz‑hozam</td>
                      <td>+/‑15° eltérés → ‑3–5 %</td>
                    </tr>
                    <tr>
                      <td>Árnyékmentesség</td>
                      <td>Árnyék &lt; 5 % a kritikus időszakban (9‑15 h)</td>
                      <td>
                        Azonnali teljesítmény és soros panelek esetén lánchúzó
                        hatás
                      </td>
                      <td>
                        Egy kémény árnyéka egész láncot visszafoghat, ‑20 %
                      </td>
                    </tr>
                    <tr>
                      <td>Sor‑távolság (földi tartó)</td>
                      <td>Pitch = panelmagasság ÷ sin dőlésszög</td>
                      <td>Téli öntárnyékolás elkerülése</td>
                      <td>Rövidített pitch → ‑10–15 % télies hozam</td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="napelemes-kisokos__note">
                <strong>Gyors szabály:</strong> Ha dél helyett teljesen
                kelet‑nyugat tájolás (90°/270°), ugyanannyi panellel ≈ 18–22
                %‑kal kisebb éves termelést kapsz – viszont hosszabb
                időablakban, ami kedvez a nappali fogyasztásnak.
              </motion.p>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}>
                4.2 Tartószerkezet és szakszerű telepítés
              </motion.h3>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}>
                <h4>Miért nem mindegy, hogyan fogják fel a paneleket?</h4>
                <ul>
                  <li>
                    Statikai biztonság – szél‑ (≤ 2400 Pa) és hóterhelésre (&lt;
                    5400 Pa) méretezett alumínium sínrendszer (EN 1991‑1‑4, EN
                    1991‑1‑3).
                  </li>
                  <li>
                    Tetőintegritás – csavarok/kampó a szarufába; EPDM‑tömítés +
                    lemezvillogó a beázás ellen.
                  </li>
                  <li>
                    Galvanikus korrózió elkerülése – alumínium sín + saválló
                    A2/A4 inox csavar, elválasztó alátét acéllemezhez.
                  </li>
                  <li>
                    Hő‑ és dilatációkompenzáció – 2 mm hézag/sín‑csavar, hosszú
                    rendszereknél dilatációs illesztés.
                  </li>
                  <li>
                    Villám‑ és érintésvédelem – MSZ HD 60364‑7‑712 szerinti
                    földelés; egyenpotenciálba hozás ≤ 10 Ω.
                  </li>
                  <li>
                    Esztétika és karbantarthatóság – kábelcsatorna a sínen
                    belül, 30 mm lég¬rés a tető és panel között hűtésre.
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}>
                <h4>Professzionális telepítés lépései</h4>
                <ol>
                  <li>
                    Statikai számítás – tető rétegrend + rögzítési pontok
                    kiértékelése (Eurocode és TERC szoftver).
                  </li>
                  <li>
                    Rögzítőelemek beépítése – nyomatékkulcs 8‑12 Nm; minden 2.
                    sínkapocs ellenőrizve.
                  </li>
                  <li>
                    Sínek igazítása – lézervetővel ±2 mm síkfekvés, hogy ne
                    feszüljön a panel.
                  </li>
                  <li>
                    Panelek felhelyezése – gyártói bilincs¬torque betartása
                    (10–14 Nm), földelő fogazott alátét a köztes bilinccsel.
                  </li>
                  <li>
                    Kábelezés – UV‑álló, halogénmentes H1Z2Z2‑K kábel, IP67 MC4
                    csatlakozó + kábelbilincs 40 cm‑enként.
                  </li>
                  <li>
                    Dokumentáció és fotó – átadáskor statikai jegyzőkönyv,
                    kötőelemlista, hőkamerás kép.
                  </li>
                </ol>
                <p className="napelemes-kisokos__warning">
                  Ne spórolj a tartón! Egy rosszul rögzített panel 120 km/h‑ás
                  szélben tetőcserépre zuhanva több kárt okozhat, mint a
                  tartósín ára.
                </p>
              </motion.div>
            </section>

            {/* Section 5 */}
            <section id="section5" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                5. Nettó vs. Bruttó elszámolás (2025)
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                Lényeg röviden: A nettó elszámolásnál az éves mérleged a döntő,
                a bruttónál minden kilowattórát „elad‑veszel" a szolgáltatóval
                perces pontossággal.
              </motion.p>

              <motion.div
                className="napelemes-kisokos__table"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Jellemző</th>
                      <th>Nettó (szaldó)</th>
                      <th>Bruttó (visszatáplálási)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Ki jogosult?</td>
                      <td>
                        Aki 2024. jan. 1. előtt betáplálási engedélyt szerzett
                        (átmeneti védettség 2026‑ig).
                      </td>
                      <td>Minden új rendszer 2024‑től.</td>
                    </tr>
                    <tr>
                      <td>Mérési elv</td>
                      <td>Éves szaldó: termelés – fogyasztás = nettó kWh.</td>
                      <td>
                        Időszakos (15 perc) export‑import külön számlázva.
                      </td>
                    </tr>
                    <tr>
                      <td>Átvételi ár</td>
                      <td>Azonos a vásárolt áram árával (kvázi 1:1).</td>
                      <td>
                        Fix feed‑in tarifa (38 Ft/kWh 2025‑ben) – alacsonyabb,
                        mint a lakossági áramár.
                      </td>
                    </tr>
                    <tr>
                      <td>Optimalizálás</td>
                      <td>
                        Nagyobb rendszer is telepíthető, „elsimul" évesen.
                      </td>
                      <td>
                        Pontos méretezés, fókusz a nappali fogyasztásra és
                        tárolásra.
                      </td>
                    </tr>
                    <tr>
                      <td>Akkumulátor</td>
                      <td>Opcionális (szaldóban kevésbé indokolt).</td>
                      <td>
                        Rendkívül hasznos: nappali többlet → esti saját
                        fogyasztás.
                      </td>
                    </tr>
                    <tr>
                      <td>Jogszabály</td>
                      <td>
                        1994. évi LXXXVI. tv. § 35 (4) mód. 387/2007. (XII. 23.)
                        Korm. rendelet
                      </td>
                      <td>
                        259/2022. (X.24.) Korm. rendelet; MEKH 7/2023. (XI.30.)
                        rendelet
                      </td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}>
                5.1 Hogyan válassz méretet bruttó alatt?
              </motion.h3>

              <motion.ol
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}>
                <li>
                  Éves fogyasztás / 1200 ≈ optimális kWp (pl. 4500 kWh → ~3,8
                  kWp).
                </li>
                <li>
                  Deye hibrid + 5–10 kWh akku → esti fogyasztás fedezése, export
                  minimalizálása.
                </li>
                <li>
                  Okos fogyasztás‑átütemezés: mosógép, EV töltés nappalra;
                  éjszakai tarifa kihasználása.
                </li>
              </motion.ol>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}>
                5.2 Átállási forgatókönyv szaldóról bruttóra
              </motion.h3>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}>
                2026. jan. 1‑jén az összes lakossági szaldós szerződés
                automatikusan bruttóra vált.
                <br />
                <strong>Tippek:</strong> akkubővítés, firmware‑frissítés, mérő
                hitelesítés.
              </motion.p>
            </section>

            {/* Section 6 */}
            <section id="section6" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                6. Inverter‑kisokos
              </motion.h2>

              <motion.div
                className="napelemes-kisokos__table"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Típus</th>
                      <th>Mikor ajánlott?</th>
                      <th>Előny</th>
                      <th>Hátrány</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>String</td>
                      <td>Egyszerű, ár‑érzékeny családi ház</td>
                      <td>Kedvező ár, bevált technológia</td>
                      <td>Árnyékérzékeny, nincs akku.</td>
                    </tr>
                    <tr>
                      <td>Mikro</td>
                      <td>Bonyolult tetők, sok árnyék</td>
                      <td>Modul‑szintű MPPT, bővíthető</td>
                      <td>Drágább, több AC‑bekötés.</td>
                    </tr>
                    <tr>
                      <td>Hibrid</td>
                      <td>Akkus vagy jövőbiztos rendszer</td>
                      <td>Egy készülék: PV → hálózat és akku és EPS</td>
                      <td>Magasabb beszerzési ár.</td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>
            </section>

            {/* Section 7 */}
            <section id="section7" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                7. Miért pont Deye hibrid inverter?
              </motion.h2>

              <motion.div
                className="napelemes-kisokos__table"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Fő előny</th>
                      <th>Mit jelent a gyakorlatban?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>97,6 % max. hatásfok</td>
                      <td>
                        Több megtermelt energia jut a konnektorba (Euro hatásfok
                        97 %).
                      </td>
                    </tr>
                    <tr>
                      <td>99,9 % MPPT hatásfok</td>
                      <td>Precíz követés változó napsütésben.</td>
                    </tr>
                    <tr>
                      <td>2–4 független MPPT, akár 26 A/string</td>
                      <td>
                        Rugalmas kelet‑dél‑nyugat tetőkön is, nagyáramú TOPCon
                        panelekhez.
                      </td>
                    </tr>
                    <tr>
                      <td>UPS‑funkció &lt; 10 ms</td>
                      <td>Számítógép, hőszivattyú nem áll le áramszünetkor.</td>
                    </tr>
                    <tr>
                      <td>Parallel bővíthetőség</td>
                      <td>Jövőben akár 160 kW‑ig skálázható.</td>
                    </tr>
                    <tr>
                      <td>Távoli leállítás + OTA firmware</td>
                      <td>Biztonság és kényelmes karbantartás.</td>
                    </tr>
                    <tr>
                      <td>IP65 és −25…+60 °C, &lt; 45 dB</td>
                      <td>Kültéren is csendes, strapabíró.</td>
                    </tr>
                    <tr>
                      <td>48 V (LV) és HV akku‑kompatibilis</td>
                      <td>Pylontech, BYD, LG ESS stb.</td>
                    </tr>
                    <tr>
                      <td>Magyar szervizhálózat (2024)</td>
                      <td>Gyors alkatrész‑ellátás, 10 év garancia‑opció.</td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>
            </section>

            {/* Section 8 */}
            <section id="section8" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                8. Telepítési folyamat 7 lépésben
              </motion.h2>

              <motion.ol
                className="napelemes-kisokos__process"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                <li>
                  Ingyenes helyszíni felmérés – tető dőlésszög, árnyékok,
                  villamos kapacitás.
                </li>
                <li>
                  Tervezés és megtérülés‑számítás – fogyasztási profil,
                  akkumulátor‑méretezés, tájolási optimalizáció.
                </li>
                <li>
                  Engedélyeztetés – hálózati csatlakozási igény, E.ON/MVM/E.On.
                </li>
                <li>
                  Beszerzés és logisztika – panelek, Deye inverter, statikailag
                  méretezett tartószerkezet.
                </li>
                <li>Szerelés – 1‑3 nap, nyomatékellenőrzés, földelés.</li>
                <li>
                  Beüzemelés és oktatás – monitoring app, inverter beállítás
                  (alap, akku, EPS).
                </li>
                <li>
                  Garancia és karbantartás – éves átvizsgálás, távdiagnosztika.
                </li>
              </motion.ol>
            </section>

            {/* Section 9 */}
            <section id="section9" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                9. Gyakori kérdések (FAQ)
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                Az alábbi kérdés‑válasz gyűjteményt két blokkba rendeztük: Alap
                (laikus) és Haladó (pro) szint, hogy mindenki megtalálja a saját
                tudásszintjének megfelelő magyarázatot.
              </motion.p>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}>
                9.1 Alap kérdések – teljesen kezdőknek
              </motion.h3>

              <motion.div
                className="napelemes-kisokos__faq"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}>
                <div className="napelemes-kisokos__table">
                  <table>
                    <thead>
                      <tr>
                        <th>Kérdés</th>
                        <th>Magyarázat egy mondatban</th>
                        <th>Bővebben</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Mi történik éjszaka?</td>
                        <td>
                          A panelek nem termelnek, ilyenkor a hálózatból vagy
                          akkumulátorból fogyasztasz.
                        </td>
                        <td>
                          Deye hibrid inverterrel az akkumulátor automatikusan
                          átveszi a terhelést, majd hajnalban újra töltődik.
                        </td>
                      </tr>
                      <tr>
                        <td>Mennyi áramot ad 1 kWp?</td>
                        <td>
                          Évi 1100–1250 kWh a magyar napsütés függvényében.
                        </td>
                        <td>
                          Ez ~ 120 € (47 000 Ft) rezsi‑megtakarítás 2025‑ös
                          lakossági tarifával.
                        </td>
                      </tr>
                      <tr>
                        <td>Kell‑e engedélyt kérni?</td>
                        <td>
                          Igen, 799 W fölött hálózati csatlakozási engedély
                          szükséges.
                        </td>
                        <td>
                          Green Island Invest Zrt intézi: E.ON/MVM online igény,
                          műszaki gazda, ad‑vesz mérő csere.
                        </td>
                      </tr>
                      <tr>
                        <td>Milyen tetőre rakható?</td>
                        <td>
                          Szinte bármilyen, a sátor‑, cserép‑, lemez‑ és
                          lapostető is alkalmas, ha statikailag ép.
                        </td>
                        <td>
                          Lapostetőre ballaszttal terhelt tartó vagy betondúcos
                          konzol kerül.
                        </td>
                      </tr>
                      <tr>
                        <td>Mi történik felhős időben?</td>
                        <td>A termelés 10–40 %-ra esik, de nem áll le.</td>
                        <td>
                          Diffúz fényben is dolgoznak a cellák, a hibrid
                          inverter pedig optimalizálja a pontot.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}>
                9.2 Haladó kérdések – technikai érdeklődőknek
              </motion.h3>

              <motion.div
                className="napelemes-kisokos__faq"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}>
                <div className="napelemes-kisokos__table">
                  <table>
                    <thead>
                      <tr>
                        <th>Kérdés</th>
                        <th>Rövid válasz</th>
                        <th>Részletesebb magyarázat</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          Mi a különbség PERC, TOPCon és HJT modulok között?
                        </td>
                        <td>
                          Hatásfokban és gyártástechnológiában: HJT &lt; TOPCon
                          &lt; PERC.
                        </td>
                        <td>
                          TOPCon 22 % fölötti, HJT 24 %‑ot is tud, de drágább;
                          Deye max. 26 A/string miatt mindet kezeli.
                        </td>
                      </tr>
                      <tr>
                        <td>Mit jelent a 0 export mód?</td>
                        <td>
                          Az inverter a hálózat felé nulla betáplálást tart
                          fenn.
                        </td>
                        <td>
                          Kötelező ott, ahol a hálózat telített; Deye
                          CT‑szenzorral ± 1 % pontossággal szabályoz.
                        </td>
                      </tr>
                      <tr>
                        <td>Miért jobb a 3 fázisú inverter?</td>
                        <td>
                          Egyenletesebb terhelés, nagyobb engedélyezett
                          teljesítmény.
                        </td>
                        <td>
                          3× 16 A betáplálás → 10 kW fölötti rendszerek, motoros
                          terhelés egyenletes járása.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>

              <motion.p
                className="napelemes-kisokos__tip"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}>
                <strong>Tipp:</strong> Ha nem találod a válaszodat, írj a
                info@greenislandinvest.hu címre – 24 órán belül reagálunk.
              </motion.p>
            </section>

            {/* Section 10 */}
            <section id="section10" className="napelemes-kisokos__section">
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}>
                10. Okos megoldások
              </motion.h2>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}>
                10.1 Okos otthon ≠ luxus, hanem energiamenedzsment
              </motion.h3>

              <motion.ul
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}>
                <li>
                  Valós idejű fogyasztás‑optimalizálás – a napelemes termelési
                  görbétől függően a Green Island Invest Zrt által telepített
                  okos‑relék és Home Energy Management System (HEMS)
                  automatikusan indítja a mosást, hőszivattyút vagy EV‑töltést,
                  így bruttó elszámolásban ≈ +15 % saját fogyasztás érhető el.
                </li>
                <li>
                  Peak‑shaving és tarifaváltás – tároló + HEMS együtt képes
                  eltolni a csúcsidőszakokat, mérsékelve a hálózat
                  igénybevételét.
                </li>
                <li>
                  Megnyugtató szünetmentesség – áramszünetkor az UPS‑képes Deye
                  fázisszétválasztással csak a kritikus okos‑köröket tartja
                  életben (hűtő, router, riasztó), így több órás blackout‑álló
                  otthon.
                </li>
                <li>
                  IoT‑integráció – Alexa/Google Home, Home Assistant, KNX – a
                  Green Island Invest Zrt mérnökei tanúsított integrátorok, így
                  a rendszer a felhasználó jelenléte, időjárás vagy
                  energiaár‑API alapján is dönt.
                </li>
              </motion.ul>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}>
                10.2 Energiaközösségek: a jövő kollektív naperőműve
              </motion.h3>

              <motion.div
                className="napelemes-kisokos__table"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Fogalom</th>
                      <th>Magyar jogi definíció (2023 LXXX. tv.)</th>
                      <th>Mit jelent a gyakorlatban?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Megújulóenergia‑közösség (RE‑kom)</td>
                      <td>
                        Termelés, tárolás, elosztás, energia‑megosztás legalább
                        két, földrajzilag közeli tag között.
                      </td>
                      <td>
                        Kisvárosi lakópark vagy társasház közös tetőnapeleme +
                        közös akku, belső elszámolórendszerrel.
                      </td>
                    </tr>
                    <tr>
                      <td>Polgári energiaközösség</td>
                      <td>
                        Lakossági tagok nonprofit társasága, max. 30 MW
                        termelőképesség.
                      </td>
                      <td>
                        Falu bioerőmű + napelempark, a tagok – akár okos mérőn
                        át – áramot kapnak kedvezményesen.
                      </td>
                    </tr>
                    <tr>
                      <td>Prosumer aggregátum</td>
                      <td>
                        Több száz háztartás online összefogása a flexibilis
                        terhelések cseréjére.
                      </td>
                      <td>
                        A Green Island Invest Zrt pilot‑projektje 2025‑ben – 350
                        ház, közös virtuális erőmű, hálózati kiegyenlítési
                        bevétel.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>

              <motion.p
                className="napelemes-kisokos__highlight"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}>
                <strong>Miért fontos?</strong> 2024‑től a bruttó elszámolásban
                értékesített „közösségi" kWh‑k után 10 Ft/kWh adókedvezmény jár;
                a Green Island Invest Zrt teljes körű jogi‑mérnöki hátteret
                biztosít a közösség‑szervezéstől a mérlegkör‑kezelésig.
              </motion.p>

              <motion.p
                className="napelemes-kisokos__cta"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}>
                <strong>Következő lépés:</strong> Kérj okos otthon + közösségi
                energia auditot a Green Island Invest Zrt‑től, és tudj meg
                mindent arról, hogyan tud a tetőd, a villanyórád és a szomszédod
                napeleme együttműködni.
              </motion.p>
            </section>
          </div>
        </div>

        <CompanyContact
          onCallback={() => chatButton.current.openChat()}
        />
      </div>
      <FloatingChatButton ref={chatButton} />
    </>
  );
};

export default NapelemesKisokosModel;
